import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import {
  Calendar as CalendarIcon,
  SlidersHorizontal,
  MoreHorizontal,
  Eye,
  Trash,
  FileText,
  CheckCircle,
  CircleAlert,
  Filter,
  X,
  Loader2,
  Plus,
  Send,
  TruckIcon,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { navigate } from "wouter/use-browser-location";
import { useSalesOrders, useDeleteSalesOrder, useCloneSalesOrder, useSendSalesOrderEmail } from "@/hooks/useSalesOrders";
import { SalesOrderListItem, SalesOrderStatus } from "@/types/salesDocuments";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

const cn = (...classes: (string | boolean)[]) =>
  classes.filter(Boolean).join(" ");

export const SalesOrders = () => {
  const { formatTotalAmount } = useDecimalPrecision();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [filter, setFilter] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [dateRange, setDateRange] = React.useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });
  const [selectedSalesOrders, setSelectedSalesOrders] = React.useState<string[]>([]);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [visibleColumns, setVisibleColumns] = React.useState({
    codeNumber: true,
    buyer: true,
    orderDate: true,
    expectedDeliveryDate: true,
    customerPONumber: true,
    amount: true,
    status: true,
    deliveryStatus: true,
    emailStatus: true,
    actions: true,
  });
  const [dropdownOpenVisibleColumn, setDropdownOpenVisibleColumn] = React.useState(false);
  const [openDropdownRowId, setOpenDropdownRowId] = React.useState<string | null>(null);

  const itemsPerPage = 10;

  const { data: salesOrdersData, isLoading } = useSalesOrders();
  const deleteSalesOrderMutation = useDeleteSalesOrder();
  const cloneSalesOrderMutation = useCloneSalesOrder();
  const sendEmailMutation = useSendSalesOrderEmail();

  const salesOrders = salesOrdersData?.data || [];

  const statuses = [
    "all",
    SalesOrderStatus.DRAFT,
    SalesOrderStatus.CONFIRMED,
    SalesOrderStatus.PROCESSING,
    SalesOrderStatus.PARTIALLY_DELIVERED,
    SalesOrderStatus.DELIVERED,
    SalesOrderStatus.CANCELLED,
  ];

  // Reset pagination when filters change
  const resetPagination = React.useCallback(() => {
    setCurrentPage(1);
  }, []);

  React.useEffect(() => {
    resetPagination();
  }, [filter, statusFilter, dateRange]);

  // Enhanced filter logic
  const filteredData = salesOrders.filter((salesOrder: SalesOrderListItem) => {
    const buyerName = salesOrder.buyerName ?? "Not Found";

    const matchesSearch =
      buyerName.toLowerCase().includes(filter.toLowerCase()) ||
      salesOrder.codeNumber.toLowerCase().includes(filter.toLowerCase()) ||
      salesOrder.customerPONumber?.toLowerCase().includes(filter.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || salesOrder.status === statusFilter;

    const orderDate = new Date(salesOrder.orderDate);
    const matchesDateRange =
      (!dateRange.from || orderDate >= dateRange.from) &&
      (!dateRange.to || orderDate <= dateRange.to);

    return matchesSearch && matchesStatus && matchesDateRange;
  });

  // Calculate statistics
  const stats = {
    totalAmount: filteredData.reduce(
      (sum: number, so: SalesOrderListItem) => sum + Number(so.totalPayableAmount),
      0
    ),
    totalSalesOrders: filteredData.length,
    draftSalesOrders: filteredData.filter(
      (so: SalesOrderListItem) => so.status === SalesOrderStatus.DRAFT
    ).length,
    confirmedSalesOrders: filteredData.filter(
      (so: SalesOrderListItem) => so.status === SalesOrderStatus.CONFIRMED
    ).length,
    deliveredSalesOrders: filteredData.filter(
      (so: SalesOrderListItem) => so.status === SalesOrderStatus.DELIVERED
    ).length,
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSalesOrders(filteredData.map((so: SalesOrderListItem) => so.id));
    } else {
      setSelectedSalesOrders([]);
    }
  };

  const handleSelectSalesOrder = (checked: string | boolean, id: string) => {
    if (checked) {
      setSelectedSalesOrders([...selectedSalesOrders, id]);
    } else {
      setSelectedSalesOrders(selectedSalesOrders.filter((selectedId) => selectedId !== id));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteSalesOrderMutation.mutateAsync(id);
      toast({
        title: "Deleted",
        description: "Sales Order has been deleted",
        icon: <CheckCircle size={30} className="animate-spin-once text-green-600" />,
        duration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ["salesOrders"] });
    } catch (error: any) {
      toast({
        title: "Action failed",
        variant: "destructive",
        description: error.response?.data?.message || "Failed to delete sales order",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 5000,
      });
    }
  };

  const handleClone = async (id: string) => {
    try {
      await cloneSalesOrderMutation.mutateAsync(id);
      toast({
        title: "Success",
        description: "Sales Order has been cloned",
        icon: <CheckCircle size={30} className="animate-spin-once text-green-600" />,
        duration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ["salesOrders"] });
    } catch (error: any) {
      toast({
        title: "Action failed",
        variant: "destructive",
        description: error.response?.data?.message || "Failed to clone sales order",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 5000,
      });
    }
  };

  const handleBatchClone = async () => {
    let successCount = 0;
    for (const id of selectedSalesOrders) {
      try {
        await cloneSalesOrderMutation.mutateAsync(id);
        successCount++;
      } catch {
        // continue
      }
    }
    toast({
      title: "Clone Complete",
      description: `${successCount} sales order(s) cloned successfully`,
      icon: <CheckCircle size={30} className="animate-spin-once text-green-600" />,
      duration: 5000,
    });
    queryClient.invalidateQueries({ queryKey: ["salesOrders"] });
    setSelectedSalesOrders([]);
  };

  const handleBatchSendEmail = async () => {
    try {
      await sendEmailMutation.mutateAsync(selectedSalesOrders);
      toast({
        title: "Sending",
        description: `Sending email for ${selectedSalesOrders.length} sales order(s)`,
        icon: <CheckCircle size={30} className="animate-spin-once text-green-600" />,
        duration: 5000,
      });
      setSelectedSalesOrders([]);
    } catch (error: any) {
      toast({
        title: "Action failed",
        variant: "destructive",
        description: error.response?.data?.message || "Failed to send emails",
        icon: <CircleAlert size={30} className="animate-spin-once" />,
        duration: 5000,
      });
    }
  };

  const getEmailStatusColor = (status: string | null) => {
    const base = "rounded-full px-3 py-1 text-xs font-medium";
    switch (status) {
      case "Sending": return `${base} bg-yellow-100 text-yellow-800`;
      case "Sent": return `${base} bg-green-100 text-green-800`;
      case "Error": return `${base} bg-red-100 text-red-800`;
      default: return `${base} bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  const getStatusColor = (status: SalesOrderStatus) => {
    const baseStyle = "rounded-full px-3 py-1 text-xs font-medium";
    switch (status) {
      case SalesOrderStatus.DRAFT:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
      case SalesOrderStatus.CONFIRMED:
        return `${baseStyle} bg-blue-100 text-blue-800`;
      case SalesOrderStatus.PROCESSING:
        return `${baseStyle} bg-yellow-100 text-yellow-800`;
      case SalesOrderStatus.PARTIALLY_DELIVERED:
        return `${baseStyle} bg-orange-100 text-orange-800`;
      case SalesOrderStatus.DELIVERED:
        return `${baseStyle} bg-green-100 text-green-800`;
      case SalesOrderStatus.CANCELLED:
        return `${baseStyle} bg-red-100 text-red-800`;
      default:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  const getDeliveryStatusColor = (status: string) => {
    const baseStyle = "rounded-full px-3 py-1 text-xs font-medium";
    switch (status) {
      case "Not Started":
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
      case "Partial":
        return `${baseStyle} bg-orange-100 text-orange-800`;
      case "Complete":
        return `${baseStyle} bg-green-100 text-green-800`;
      default:
        return `${baseStyle} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`;
    }
  };

  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisibleButtons = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisibleButtons / 2));
    const endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    if (startPage > 1) {
      buttons.push(
        <Button
          key="1"
          variant={"outline"}
          size="sm"
          onClick={() => setCurrentPage(1)}
        >
          1
        </Button>
      );

      if (startPage > 2) {
        buttons.push(<span key="ellipsis-start">...</span>);
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      buttons.push(
        <Button
          key={page}
          variant={currentPage === page ? "default" : "outline"}
          size="sm"
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis-end">...</span>);
      }

      buttons.push(
        <Button
          key={totalPages}
          variant={"outline"}
          size="sm"
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Button>
      );
    }

    return buttons;
  };

  const activeFilters = [
    statusFilter !== "all" && { type: "Status", value: statusFilter },
    dateRange.from && { type: "From", value: format(dateRange.from, "PP") },
    dateRange.to && { type: "To", value: format(dateRange.to, "PP") },
  ].filter(Boolean);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Sales Orders</h1>
        <Button onClick={() => navigate("/sales-order/new")}>
          <Plus className="mr-2 h-4 w-4" />
          Create Sales Order
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total SO Amount</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              RM {formatTotalAmount(stats.totalAmount)}
            </div>
            <p className="text-xs text-muted-foreground">Total sales order value</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total SOs</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalSalesOrders}</div>
            <p className="text-xs text-muted-foreground">Across all statuses</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.draftSalesOrders}</div>
            <p className="text-xs text-muted-foreground">Draft orders</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Confirmed</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.confirmedSalesOrders}</div>
            <p className="text-xs text-muted-foreground">Confirmed orders</p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Fully Delivered</CardTitle>
            <TruckIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.deliveredSalesOrders}</div>
            <p className="text-xs text-muted-foreground">Completed deliveries</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Controls */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex flex-1 items-center gap-4">
            <Input
              placeholder="Search SO number, buyer, PO number..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="max-w-sm"
            />

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses
                  .filter((status) => status !== "all")
                  .map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-[240px] justify-start text-left font-normal",
                    !dateRange.from && !dateRange.to && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateRange.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, "PP")} - {format(dateRange.to, "PP")}
                      </>
                    ) : (
                      format(dateRange.from, "PP")
                    )
                  ) : (
                    "Pick a date range"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) =>
                    setDateRange({ from: range?.from, to: range?.to })
                  }
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>

          <DropdownMenu
            open={dropdownOpenVisibleColumn}
            onOpenChange={(val) => setDropdownOpenVisibleColumn(val)}
          >
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-4">
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(visibleColumns).map(([key, value]) => (
                <DropdownMenuCheckboxItem
                  key={key}
                  className="capitalize"
                  checked={value}
                  onCheckedChange={(checked) =>
                    setVisibleColumns((prev) => ({ ...prev, [key]: checked }))
                  }
                >
                  {key}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Batch actions */}
        <div className="h-9">
          {selectedSalesOrders.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {selectedSalesOrders.length} selected
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={handleBatchSendEmail}
                disabled={sendEmailMutation.isPending}
              >
                {sendEmailMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Send Email
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleBatchClone}
                disabled={cloneSalesOrderMutation.isPending}
              >
                {cloneSalesOrderMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                Clone
              </Button>
            </div>
          )}
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {activeFilters.map((filter) => (
              <Badge
                key={filter ? `${filter.type}-${filter.value}` : undefined}
                variant="secondary"
                className="flex items-center gap-1"
              >
                {filter && `${filter.type}: ${filter.value}`}
                <X
                  className="h-3 w-3 cursor-pointer"
                  onClick={() => {
                    if (filter) {
                      switch (filter.type) {
                        case "Status":
                          setStatusFilter("all");
                          break;
                        case "From":
                        case "To":
                          setDateRange({ from: undefined, to: undefined });
                          break;
                      }
                    }
                  }}
                />
              </Badge>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStatusFilter("all");
                setDateRange({ from: undefined, to: undefined });
                setFilter("");
              }}
            >
              Clear all filters
            </Button>
          </div>
        )}
      </div>

      {/* Empty State */}
      {filteredData.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-100">
            <Filter className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="mt-4 font-semibold">No sales orders found</h3>
          <p className="mt-2 text-sm text-gray-500">
            {filter || statusFilter !== "all" || dateRange.from
              ? "Try adjusting your search or filter to find what you're looking for."
              : "Get started by creating your first sales order."}
          </p>
          {!filter && statusFilter === "all" && !dateRange.from && (
            <Button
              className="mt-4"
              onClick={() => navigate("/sales-order/new")}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Sales Order
            </Button>
          )}
        </div>
      )}

      {/* Table */}
      {filteredData.length > 0 && (
        <>
          <div className="rounded-md border shadow-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        selectedSalesOrders.length === filteredData.length &&
                        selectedSalesOrders.length !== 0
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  {visibleColumns.codeNumber && <TableHead>SO Number</TableHead>}
                  {visibleColumns.buyer && <TableHead>Buyer</TableHead>}
                  {visibleColumns.orderDate && <TableHead>Order Date</TableHead>}
                  {visibleColumns.expectedDeliveryDate && <TableHead>Expected Delivery</TableHead>}
                  {visibleColumns.customerPONumber && <TableHead>Customer PO</TableHead>}
                  {visibleColumns.amount && <TableHead className="text-right">Amount</TableHead>}
                  {visibleColumns.status && <TableHead>Status</TableHead>}
                  {visibleColumns.deliveryStatus && <TableHead>Delivery Status</TableHead>}
                  {visibleColumns.emailStatus && <TableHead>Email Status</TableHead>}
                  {visibleColumns.actions && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((salesOrder: SalesOrderListItem) => (
                  <TableRow
                    key={salesOrder.id}
                    onDoubleClick={() => navigate(`/sales-order/view`, { state: { id: salesOrder.id } })}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedSalesOrders.includes(salesOrder.id)}
                        onCheckedChange={(checked) =>
                          handleSelectSalesOrder(checked, salesOrder.id)
                        }
                      />
                    </TableCell>
                    {visibleColumns.codeNumber && (
                      <TableCell className="font-medium">{salesOrder.codeNumber}</TableCell>
                    )}
                    {visibleColumns.buyer && (
                      <TableCell>
                        {salesOrder.buyerName || "Not Found"}
                        <div className="text-xs text-gray-500">{salesOrder.buyerCode}</div>
                      </TableCell>
                    )}
                    {visibleColumns.orderDate && (
                      <TableCell>
                        {format(new Date(salesOrder.orderDate), "dd/MM/yyyy")}
                      </TableCell>
                    )}
                    {visibleColumns.expectedDeliveryDate && (
                      <TableCell>
                        {salesOrder.expectedDeliveryDate
                          ? format(new Date(salesOrder.expectedDeliveryDate), "dd/MM/yyyy")
                          : "-"}
                      </TableCell>
                    )}
                    {visibleColumns.customerPONumber && (
                      <TableCell>{salesOrder.customerPONumber || "-"}</TableCell>
                    )}
                    {visibleColumns.amount && (
                      <TableCell className="text-right">
                        {formatTotalAmount(Number(salesOrder.totalPayableAmount))}
                      </TableCell>
                    )}
                    {visibleColumns.status && (
                      <TableCell>
                        <span className={getStatusColor(salesOrder.status)}>
                          {salesOrder.status}
                        </span>
                      </TableCell>
                    )}
                    {visibleColumns.deliveryStatus && (
                      <TableCell>
                        <span className={getDeliveryStatusColor(salesOrder.deliveryStatus)}>
                          {salesOrder.deliveryStatus}
                        </span>
                      </TableCell>
                    )}
                    {visibleColumns.emailStatus && (
                      <TableCell>
                        {salesOrder.emailStatus ? (
                          <span className={getEmailStatusColor(salesOrder.emailStatus)}>
                            {salesOrder.emailStatus}
                          </span>
                        ) : null}
                      </TableCell>
                    )}
                    {visibleColumns.actions && (
                      <TableCell>
                        <DropdownMenu
                          open={openDropdownRowId === salesOrder.id}
                          onOpenChange={(isOpen) =>
                            setOpenDropdownRowId(isOpen ? salesOrder.id : null)
                          }
                        >
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <Button
                              variant="ghost"
                              onClick={() => navigate(`/sales-order/view`, { state: { id: salesOrder.id } })}
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              View Details
                            </Button>
                            <DropdownMenuSeparator />
                            <Button
                              variant="ghost"
                              className="w-full justify-start"
                              onClick={() => handleClone(salesOrder.id)}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Clone
                            </Button>
                            <DropdownMenuSeparator />
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  className="w-full justify-start text-red-600"
                                  variant="ghost"
                                >
                                  <Trash className="mr-2 h-4 w-4" />
                                  Delete
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Sales Order</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete sales order{" "}
                                    {salesOrder.codeNumber}? This action cannot be undone.
                                    {salesOrder.quotationNumber && (
                                      <span className="block mt-2 text-sm">
                                        Note: The linked quotation will be reverted to Draft status.
                                      </span>
                                    )}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(salesOrder.id)}
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          <div className="flex items-center justify-between px-2">
            <div className="text-sm text-gray-500">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, filteredData.length)} of{" "}
              {filteredData.length} results
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              {getPaginationButtons()}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SalesOrders;
